import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useBrokerLogin } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/auth';
import { getErrorMessage } from '@/services';
import { isTwoFactorRequired, TwoFactorCodeField } from './twoFactorStep';

const loginSchema = z.object({
  identifier: z.string().min(1, 'Email or phone is required'),
  password: z.string().min(1, 'Password is required'),
  // Optional because it only exists on the SECOND submit of the same form:
  // the server asks for it after the password has already been accepted.
  two_factor_code: z.string().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function BrokerLoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const loginMutation = useBrokerLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // 9b: the second step of this same form. The mutation's last error is the
  // only state it needs -- no separate page, no stashed credentials.
  const needsSecondFactor = isTwoFactorRequired(loginMutation.error);
  const secondFactorMessage = needsSecondFactor ? getErrorMessage(loginMutation.error) : undefined;

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = (data: LoginFormData) => {
    const isEmail = data.identifier.includes('@');
    loginMutation.mutate({
      ...(isEmail ? { email: data.identifier } : { phone: data.identifier }),
      password: data.password,
      ...(data.two_factor_code ? { two_factor_code: data.two_factor_code.trim() } : {}),
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-emerald-100 py-12 px-4 sm:px-6 lg:px-8 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 dark:bg-gray-800">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4 dark:bg-emerald-900/30 dark:text-emerald-300">
              <UserGroupIcon className="h-8 w-8" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Broker Portal</h1>
            <p className="text-gray-500 mt-1 dark:text-gray-400">Sign in to your broker account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Email or Phone
              </label>
              <input
                {...register('identifier')}
                type="text"
                id="identifier"
                className="input"
                placeholder="Enter email or phone number"
                autoComplete="username"
              />
              {errors.identifier && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.identifier.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Password
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  data-custom-reveal="true"
                  id="password"
                  className="input pr-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
              )}
            </div>

            {needsSecondFactor && (
              <TwoFactorCodeField
                registration={register('two_factor_code')}
                message={secondFactorMessage}
                accentClassName="text-emerald-600"
              />
            )}

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded dark:border-gray-600"
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full btn bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginMutation.isPending ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/login-select"
              className="block text-center text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              &larr; Back to login options
            </Link>
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
          &copy; {new Date().getFullYear()} base.Policy.online. All rights reserved.
        </p>
      </div>
    </div>
  );
}
